import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import type { Page } from '@/components/canvas/document/publicTypes';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import type { WorkspacePlayConfig } from '@/components/canvas/workspace/types';
import {
  PRESENTATION_DEFAULT_INTERVAL_SECONDS,
  PRESENTATION_INTERVAL_MAX_SECONDS,
  PRESENTATION_INTERVAL_MIN_SECONDS,
} from '@/components/canvas/workspace/viewport/presentationConstants';

export interface UseWorkspaceFullscreenOptions {
  workspaceRef: React.RefObject<HTMLDivElement | null>;
  stageHostRef: React.RefObject<HTMLDivElement | null>;
  pages: Page[];
  currentPageId: string;
  setCurrentPageId: (id: string) => void;
  autoEnterFullscreen?: boolean;
  playConfig?: WorkspacePlayConfig;
  getFitSize: () => { w: number; h: number } | null;
  runFitWithTransition: (w: number, h: number, noMargin?: boolean) => void;
}

export function useWorkspaceFullscreen({
  workspaceRef,
  stageHostRef,
  pages,
  currentPageId,
  setCurrentPageId,
  autoEnterFullscreen = false,
  playConfig,
  getFitSize,
  runFitWithTransition,
}: UseWorkspaceFullscreenOptions) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackBarVisible, setPlaybackBarVisible] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [loop, setLoop] = useState(false);
  const [playbackIntervalSeconds, setPlaybackIntervalSeconds] = useState(
    PRESENTATION_DEFAULT_INTERVAL_SECONDS,
  );

  const { resolvedTheme, setTheme } = useTheme();
  const themeBeforePresentationRef = useRef<string | null>(null);
  const autoFullscreenAppliedRef = useRef(false);
  const playConfigRef = useRef<WorkspacePlayConfig | undefined>(undefined);
  playConfigRef.current = playConfig;

  useEffect(() => {
    if (!autoEnterFullscreen || autoFullscreenAppliedRef.current) return;
    if (pages.length === 0) return;
    const id = requestAnimationFrame(() => {
      const el = workspaceRef.current;
      if (!el || document.fullscreenElement) return;
      autoFullscreenAppliedRef.current = true;
      themeBeforePresentationRef.current = resolvedTheme ?? 'light';
      setTheme('dark');
      el.requestFullscreen().catch(() => {});
    });
    return () => cancelAnimationFrame(id);
  }, [autoEnterFullscreen, pages.length, resolvedTheme, setTheme, workspaceRef]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const isNow =
        !!document.fullscreenElement && document.fullscreenElement === workspaceRef.current;
      if (isNow) {
        if (pages.length > 0) {
          setCurrentPageId(pages[0].id);
        }
        const cfg = playConfigRef.current;
        if (cfg) {
          setAutoPlay(!!cfg.autoPlay);
          if (typeof cfg.intervalSeconds === 'number' && Number.isFinite(cfg.intervalSeconds)) {
            setPlaybackIntervalSeconds(
              Math.max(
                PRESENTATION_INTERVAL_MIN_SECONDS,
                Math.min(PRESENTATION_INTERVAL_MAX_SECONDS, cfg.intervalSeconds),
              ),
            );
          }
          if (cfg.workspacePageLayoutMode) {
            useCanvasStore.getState().setWorkspacePageLayoutMode(cfg.workspacePageLayoutMode);
          }
        }
      } else {
        setPlaybackBarVisible(false);
        setAutoPlay(false);
        setLoop(false);
        setPlaybackIntervalSeconds(PRESENTATION_DEFAULT_INTERVAL_SECONDS);
        if (pages.length > 0) {
          setCurrentPageId(pages[0].id);
        }
      }
      setIsFullscreen(isNow);
      if (isNow) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const viewport = stageHostRef.current;
            if (viewport && viewport.clientWidth > 0 && viewport.clientHeight > 0) {
              runFitWithTransition(viewport.clientWidth, viewport.clientHeight, true);
            }
          });
        });
      } else {
        const prevTheme = themeBeforePresentationRef.current;
        themeBeforePresentationRef.current = null;
        if (prevTheme) setTheme(prevTheme);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = workspaceRef.current;
            const w = el?.clientWidth ?? 0;
            const h = el?.clientHeight ?? 0;
            if (w > 0 && h > 0) {
              runFitWithTransition(w, h);
            }
          });
        });
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [
    setTheme,
    pages,
    currentPageId,
    setCurrentPageId,
    workspaceRef,
    stageHostRef,
    runFitWithTransition,
  ]);

  const handleToggleFullscreen = useCallback(() => {
    if (!workspaceRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    if (pages.length === 0) return;
    themeBeforePresentationRef.current = resolvedTheme ?? 'light';
    setTheme('dark');
    workspaceRef.current.requestFullscreen();
  }, [pages.length, resolvedTheme, setTheme, workspaceRef]);

  useEffect(() => {
    if (!isFullscreen || pages.length > 0) return;
    if (document.fullscreenElement === workspaceRef.current) {
      void document.exitFullscreen();
    }
  }, [isFullscreen, pages.length, workspaceRef]);

  const handlePreviousCanvas = useCallback(() => {
    if (pages.length === 0) return;
    const idx = pages.findIndex((c) => c.id === currentPageId);
    if (idx > 0) {
      setCurrentPageId(pages[idx - 1].id);
    } else if (pages.length > 1) {
      setCurrentPageId(pages[pages.length - 1].id);
    }
  }, [pages, currentPageId, setCurrentPageId]);

  const handleNextCanvas = useCallback(() => {
    if (pages.length === 0) return;
    const idx = pages.findIndex((c) => c.id === currentPageId);
    if (idx >= 0 && idx < pages.length - 1) {
      setCurrentPageId(pages[idx + 1].id);
    } else if (pages.length > 1) {
      setCurrentPageId(pages[0].id);
    }
  }, [pages, currentPageId, setCurrentPageId]);

  const advanceOrStop = useCallback(() => {
    if (pages.length <= 1) return;
    const idx = pages.findIndex((c) => c.id === currentPageId);
    if (idx < 0) return;
    if (idx < pages.length - 1) {
      setCurrentPageId(pages[idx + 1].id);
      return;
    }
    setCurrentPageId(pages[0].id);
  }, [pages, currentPageId, setCurrentPageId]);

  useEffect(() => {
    if (!autoPlay || !isFullscreen || pages.length <= 1) return;
    const ms =
      Math.max(
        PRESENTATION_INTERVAL_MIN_SECONDS * 1000,
        Math.min(PRESENTATION_INTERVAL_MAX_SECONDS * 1000, playbackIntervalSeconds * 1000),
      );
    const t = setInterval(advanceOrStop, ms);
    return () => clearInterval(t);
  }, [autoPlay, isFullscreen, pages.length, advanceOrStop, playbackIntervalSeconds]);

  /** 播放态切换页时，按 workspaceLayout 将视口 anim 到该页 */
  useEffect(() => {
    if (!isFullscreen) return;
    const size = getFitSize();
    if (!size || size.w <= 0 || size.h <= 0) return;
    requestAnimationFrame(() => {
      runFitWithTransition(size.w, size.h, true);
    });
  }, [currentPageId, isFullscreen, getFitSize, runFitWithTransition]);

  return {
    isFullscreen,
    playbackBarVisible,
    setPlaybackBarVisible,
    autoPlay,
    setAutoPlay,
    loop,
    setLoop,
    playbackIntervalSeconds,
    setPlaybackIntervalSeconds,
    handleToggleFullscreen,
    handlePreviousCanvas,
    handleNextCanvas,
  };
}
